import type { ComponentChildren } from "preact"
import { useEffect, useState } from "preact/hooks"
import { useRevealTimer } from "../hooks/useRevealTimer"
import type { SoloHistoryItem, SoloState } from "../types"
import { IconCheck, IconX } from "./Icons"
import {
	ApiError,
	answerSoloQuestion,
	createSoloPlay,
	fetchSoloPlay,
	type SoloAnswer,
} from "./lib/api"
import { QuestionBoard } from "./QuestionBoard"
import { QuestionPicture } from "./QuestionPicture"
import { SpellBoard } from "./SpellBoard"

interface SoloPlayProps {
	id: number
	navigate: (path: string) => void
}

// How long the answered question stays on screen, colored, before advancing
// to the next one.
const REVEAL_MS = 1000

export function SoloPlay({ id, navigate }: SoloPlayProps) {
	const [state, setState] = useState<SoloState | null>(null)
	const [error, setError] = useState("")
	// The just-answered question, shown colored for REVEAL_MS before `state`
	// advances to whatever the server returned.
	const [reveal, setReveal] = useState<SoloHistoryItem | null>(null)
	// The choice just tapped, painted immediately so the answer registers on
	// touch rather than after the POST comes back — on a phone that round trip
	// is the difference between "instant" and "laggy". Correctness colours in
	// once the server replies.
	const [pending, setPending] = useState<number | null>(null)
	const [submitting, setSubmitting] = useState(false)
	const [restarting, setRestarting] = useState(false)
	const scheduleReveal = useRevealTimer(id)

	useEffect(() => {
		setState(null)
		setReveal(null)
		setError("")
		fetchSoloPlay(id)
			.then(setState)
			.catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
	}, [id])

	// One submit path for both kinds of question: the answer is either a
	// choice index or the word built out of the tiles, and everything after
	// sending it — the reveal, the 409 recovery — is the same either way.
	async function submitAnswer(answer: SoloAnswer) {
		if (!state?.question || reveal || submitting) return
		const idx = state.question.idx
		setSubmitting(true)
		if ("choice" in answer) setPending(answer.choice)
		try {
			const next = await answerSoloQuestion(id, idx, answer)
			const historyItem = next.history[next.history.length - 1]
			setReveal(historyItem)
			scheduleReveal(REVEAL_MS, () => {
				setState(next)
				setReveal(null)
				setPending(null)
				setSubmitting(false)
			})
		} catch (e) {
			if (e instanceof ApiError && e.status === 409) {
				// This tab has gone stale — the round was advanced in another
				// tab (or finished), so the server no longer accepts our answer.
				// Refetch to bring the board back to the *current* question
				// instead of leaving it stuck on an old one.
				setPending(null)
				setSubmitting(false)
				try {
					setState(await fetchSoloPlay(id))
				} catch (e2) {
					setError(e2 instanceof Error ? e2.message : "Failed to load")
				}
				return
			}
			setError(e instanceof Error ? e.message : "Failed to submit answer")
			setPending(null)
			setSubmitting(false)
		}
	}

	async function playAgain() {
		if (!state) return
		setRestarting(true)
		try {
			// Play again means the same topic *and* the same level — dropping
			// back to level 1 would quietly restart a five-year-old's counting
			// round at "up to 5".
			const next = await createSoloPlay(state.topic, state.level)
			navigate(`/solo/${next.id}`)
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to start a new round")
			setRestarting(false)
		}
	}

	if (error && !state) {
		return (
			<div class="container page">
				<div class="alert danger">{error}</div>
				<a href="/">Back home</a>
			</div>
		)
	}

	if (!state) {
		return (
			<div class="flex h-100 center jc-center">
				<div class="spinner" role="status" />
			</div>
		)
	}

	// --- Finished: show the review view ----------------------------------
	if (state.status === "finished") {
		return (
			<div class="container page" style="max-width: 40rem">
				<div class="stack gap-1">
					<h1>{state.topic_name}</h1>
					<p class="display tnum">
						{state.score} / {state.total}
					</p>
				</div>
				{error && <div class="alert danger">{error}</div>}
				<div class="flex wrap gap-2">
					<button
						type="button"
						class="primary"
						disabled={restarting}
						onClick={playAgain}
					>
						Play again
					</button>
					<a href="/" class="btn">
						Home
					</a>
				</div>
				{/* Reading, not playing: the question is the heading and the options
				    are quiet text under it. The play surface's full-width buttons
				    are deliberately not reused — filled green/red slabs outshout the
				    prompts and make the page hard to scan. */}
				{state.history.map((q) => (
					<div class="stack gap-1" key={q.idx}>
						<h3>
							{q.prompt}
							{q.image && <QuestionPicture image={q.image} variant="inline" />}
						</h3>
						{q.kind === "spell" ? (
							// The same quiet rows, with the two words in place of the
							// four options: what she built, and the right spelling.
							// No keypad here — the review is a document, not a second
							// go at the question.
							<>
								{!q.is_correct && (
									<div class="flex center gap-2 small text-danger">
										<IconX />
										<span>{q.your_text}</span>
									</div>
								)}
								<div class="flex center gap-2 small text-success">
									<IconCheck />
									<span>{q.correct_text}</span>
								</div>
							</>
						) : (
							q.choices.map((c, i) => {
								// Colour is carried by the mark and the label only. The
								// options you neither picked nor needed stay muted.
								let icon: ComponentChildren = null
								let tone = "muted"
								if (i === q.correct_choice) {
									icon = <IconCheck />
									tone = "text-success"
								} else if (i === q.your_choice) {
									icon = <IconX />
									tone = "text-danger"
								}
								return (
									<div class={`flex center gap-2 small ${tone}`} key={i}>
										{icon}
										<span>{c}</span>
									</div>
								)
							})
						)}
					</div>
				))}
			</div>
		)
	}

	// --- Active: show the play surface -----------------------------------

	// While revealing, show the just-answered question (with its correct
	// answer) instead of `state.question`, which has already moved on.
	const shown = reveal ?? state.question
	if (!shown) return null

	const yourChoice = reveal ? reveal.your_choice : null
	const correctChoice = reveal ? reveal.correct_choice : null

	const header = (
		<>
			<span class="muted">
				Question {state.current_question + 1} / {state.total}
			</span>
			<span class="muted tnum">Score: {state.score}</span>
		</>
	)
	const boardError = error && <div class="alert danger small mt-3">{error}</div>

	// The two kinds of question have two answer areas; everything above them
	// is the same shell (see BoardShell).
	if (shown.kind === "spell") {
		return (
			<SpellBoard
				// The placed tiles live in SpellBoard, so a new question needs a
				// new component. The idx is stable across the reveal, which is
				// what keeps her word on screen while it is being marked.
				key={shown.idx}
				header={header}
				prompt={shown.prompt}
				image={shown.image}
				tiles={shown.tiles}
				disabled={submitting}
				reveal={
					reveal
						? {
								text: reveal.your_text,
								isCorrect: reveal.is_correct,
								correct: reveal.correct_text,
							}
						: null
				}
				onAnswer={(text) => submitAnswer({ text })}
				footer={boardError}
			/>
		)
	}

	return (
		<QuestionBoard
			header={header}
			prompt={shown.prompt}
			image={shown.image}
			choices={shown.choices}
			choiceClass={(i) => {
				if (reveal) {
					if (i === correctChoice) return "success"
					if (i === yourChoice) return "danger"
				} else if (i === pending) {
					// Filled in the moment it is tapped, before the server answers.
					return "primary"
				}
				return "outline"
			}}
			disabled={submitting}
			onAnswer={(choice) => submitAnswer({ choice })}
			footer={boardError}
		/>
	)
}
